import React, { useEffect, useState } from 'react';
import { getTopCombis } from '../../../api/api';
import { TopCombiItem } from '../../../api/types';
import { useTranslation } from '../../../i18n';
import { SPORT_EMOJI } from '../DesktopLeftSidebar';

function formatOdds(val: string | number | undefined): string {
  if (val === undefined || val === null || val === '') return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

const SPORT_ID_TO_NAME: Record<string, string> = {
  'sr:sport:1': 'Soccer',
  'sr:sport:2': 'Basketball',
  'sr:sport:5': 'Tennis',
};

interface DesktopTopCombisPageProps {
  onAddCombi?: (item: TopCombiItem) => void;
}

export const DesktopTopCombisPage: React.FC<DesktopTopCombisPageProps> = ({ onAddCombi }) => {
  const { locale, t } = useTranslation();
  const [items, setItems] = useState<TopCombiItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    // Fetch top combis across main sports
    getTopCombis({ sport_ids: 'sr:sport:1,sr:sport:2,sr:sport:5', limit_per_sport: 20, preview_legs: 'all', locale })
      .then((res) => {
        if (!active) return;
        const flat = (res.sports || []).flatMap((s) => s.items || []);
        // Sort by bets placed (descending) as it's the primary indicator of popularity
        flat.sort((a, b) => (b.bets_placed_count || 0) - (a.bets_placed_count || 0));
        setItems(flat);
      })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [locale]);

  if (!loaded) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-tipico-red border-t-transparent" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white dark:bg-[#202020] rounded shadow-sm p-10 text-center text-gray-400 text-[15px] font-medium">
        Keine Top-Kombis verfügbar.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full pb-10">
      {items.map((item, index) => {
        const legs = (item.selections?.length ? item.selections : item.selections_preview) || [];
        const showBets = item.bets_placed_count_is_observed && (item.bets_placed_count ?? 0) > 0;
        
        return (
          <div key={item.combi_id || index} className="flex flex-col rounded-xl overflow-hidden bg-white dark:bg-[#1a1a1a] shadow-sm border border-gray-200 dark:border-white/10">
            {/* Header Stats */}
            <div className="grid grid-cols-3 bg-[#e4e7ed] dark:bg-[#2a2a2a] px-4 py-3.5 border-b border-gray-200 dark:border-white/10">
              <div className="flex flex-col items-center justify-center text-center relative">
                <span className="text-[17px] font-black text-gray-900 dark:text-white">{showBets ? item.bets_placed_count : '-'}</span>
                <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300 mt-1">Wetten platziert</span>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-10 bg-gray-300 dark:bg-white/10"></div>
              </div>
              <div className="flex flex-col items-center justify-center text-center relative">
                <span className="text-[17px] font-black text-gray-900 dark:text-white">{item.fold_size}x</span>
                <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300 mt-1">Kombi</span>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-10 bg-gray-300 dark:bg-white/10"></div>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <span className="text-[17px] font-black text-gray-900 dark:text-white">{formatOdds(item.total_odds)}</span>
                <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300 mt-1">Gesamtquote</span>
              </div>
            </div>

            {/* Legs List */}
            <div className="flex flex-col p-5 gap-3.5">
              {legs.map((sel, i) => (
                <div key={`${sel.event_id}-${sel.market_id}-${sel.outcome_id}-${i}`} className="flex items-start gap-3">
                  <div className="mt-[-2px] text-[16px] w-5 text-center flex-shrink-0 grayscale">
                    {SPORT_EMOJI[SPORT_ID_TO_NAME[item.sport_id]] || '👕'}
                  </div>
                  <div className="flex flex-col flex-1 min-w-0 -mt-[1px]">
                    <span className="text-[14px] font-bold text-gray-900 dark:text-white mb-0.5">{sel.outcome_name}</span>
                    <span className="text-[13px] text-gray-500 dark:text-gray-400 truncate">
                      {sel.event_name}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Button */}
            <div className="p-4 pt-1">
              <button
                onClick={() => onAddCombi?.(item)}
                disabled={!onAddCombi}
                className="w-full bg-[#e4e7ed] dark:bg-[#2a2a2a] hover:bg-[#d6d9df] dark:hover:bg-[#3a3a3a] text-gray-900 dark:text-white text-[14px] font-bold py-2.5 rounded-md transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Wettschein ansehen
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
